import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const CARD_MEDIA_STORAGE_KEY = 'collection-card-media-view';

/**
 * @param {HTMLElement} root
 */
function syncProductCardSlidesFromDataset(root) {
  for (const card of root.querySelectorAll('product-card')) {
    if (card instanceof HTMLElement && 'applyCollectionMediaSlide' in card) {
      /** @type {{ applyCollectionMediaSlide?: () => void }} */ (card).applyCollectionMediaSlide?.();
    }
  }
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#restoreCardMediaViewFromStorage();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncProductCardSlidesFromDataset(this);
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * @param {Event} event
   */
  updateCardMediaView({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'gsq-card-media-view' && target.name !== 'gsq-card-media-view-desktop') return;

    const value = target.value === 'model' ? 'model' : 'product';
    this.dataset.cardMediaView = value;
    sessionStorage.setItem(CARD_MEDIA_STORAGE_KEY, value);
    for (const input of this.querySelectorAll(
      'input[name="gsq-card-media-view"], input[name="gsq-card-media-view-desktop"]'
    )) {
      if (input instanceof HTMLInputElement) input.checked = input.value === value;
    }
    syncProductCardSlidesFromDataset(this);
  }

  #restoreCardMediaViewFromStorage() {
    try {
      const stored = sessionStorage.getItem(CARD_MEDIA_STORAGE_KEY);
      if (stored === 'model' || stored === 'product') {
        this.dataset.cardMediaView = stored;
        for (const input of this.querySelectorAll(
          'input[name="gsq-card-media-view"], input[name="gsq-card-media-view-desktop"]'
        )) {
          if (input instanceof HTMLInputElement) input.checked = input.value === stored;
        }
      } else {
        this.dataset.cardMediaView = 'product';
      }
    } catch {
      this.dataset.cardMediaView = 'product';
    }
  }

  /**
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#setLayout('default');
    }
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
